import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { Card, Spin, Row, Col, Typography, Alert } from "antd";
import CommonSelect from "../../../components/Common/CommonSelect";
import { useWeeklyPlanner } from "./hooks/useWeeklyPlanner";
import WeeklyPlanner from "./components/WeeklyPlanner/WeeklyPlanner";
import { CLASS_SECTION_OPTIONS, CLASS_SECTION_OPTIONS_BY_LEVEL } from "../../../constants/classConstants";
import PageHeader from "components/Common/PageHeader";
import CustomButton from "components/Common/ui/CustomButton";
import { fetchPlanningConfiguration } from "../../../redux/slicers/instanceSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import CustomAlert from "components/Common/ui/CustomAlert";
import { all_routes } from "router/all_routes";
import { useAbility } from "config/abilityContext";

const { Title, Text } = Typography;

const WeeklyPlannerPage = () => {
  const { t } = useTranslation();
  const {
    schedules,
    classes,
    fetchClassesBySection, // updated to use section instead of level
    fetchSchedulesByClassIds,
    loading,
    modalState,
    setModalState,
    handleSave,
    handleDelete,
  } = useWeeklyPlanner();
  const sectionOptions = CLASS_SECTION_OPTIONS_BY_LEVEL(t);

  const [selectedSection, setSelectedSection] = useState<string | undefined>(sectionOptions[0].value);
  const [loadingConfiguration, setLoadingConfiguration] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const ability = useAbility();

  // Fetch classes filtered by section
  useEffect(() => {
    if (selectedSection) {
      fetchClassesBySection(selectedSection);
    }
  }, [selectedSection]);

  // Fetch schedules when classes are loaded
  useEffect(() => {
    if (classes.length > 0) {
      const classIds = classes.map((cls) => cls.id);
      fetchSchedulesByClassIds(classIds);
    }
  }, [classes]);

  const handleSectionChange = (option: { value: string; label: string } | null) => {
    setSelectedSection(option?.value);
  };

  const reloadConfiguration = async () => {
    setLoadingConfiguration(true);
    await dispatch(fetchPlanningConfiguration());
    setLoadingConfiguration(false);
  }


  const breadcrumbItems = [
    { label: "dashboard", path: "/dashboard" },
    { label: "planning", active: true },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("planning_manager")}
          breadcrumbItems={breadcrumbItems}
        />
        <div>
          <Row
            gutter={[12, 12]}
            justify="end"
            align="middle"
            className="mb-3"
          >
            <Col xs={24} sm={12} md={8} lg={6} className="d-flex justify-content-end">
              <CustomButton
                onClick={async () => await reloadConfiguration()}
              >
                <i className="fa fa-refresh me-2"></i>
                {t("reload_configuration")}
              </CustomButton>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <CommonSelect
                className="w-100"
                options={sectionOptions}
                onChange={handleSectionChange}
                defaultValue={sectionOptions.find((opt) => opt.value === selectedSection)}
              />
            </Col>
          </Row>

          {(loading || loadingConfiguration) ? (
            <Row justify="center" className="mt-4">
              <Spin size="large" />
            </Row>
          ) : (
            classes?.length > 0 && (
              <WeeklyPlanner
                classes={classes}
                schedules={schedules}
                modalState={modalState}
                setModalState={setModalState}
                handleSave={handleSave}
                handleDelete={handleDelete}
                loading={loading}
              />
            ) || (
              <CustomAlert
                message={t("alert.no_class_for_section.message")}
                description={t("alert.no_class_for_section.description")}
                type="info"
                showIcon
                asLink
                to={`${all_routes.classes}?showAddModal=true`}
                buttonLabel={t("add_class")}
                canShowButton={ability.can("create", "Class") && ability.can("getAll", "Class")}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyPlannerPage;
